import { Card, Input, Spin, theme } from "antd";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import {
  DeepLinkType,
  NarrativeV1State,
} from "pages/api/deep-links/types";
import { TextTruncated } from "src/components/TextTruncated";
import { Flex } from "src/components/Flex";
import { Hover } from "src/components/Hover";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";
import { useRouter } from "next/router";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { EntityTypes } from "@vzbl/integrations";
import { SearchState } from "src/search/types";

export function ReportsLinks({
  filterable,
  filter,
  defaultTimeframe,
}: {
  filterable: string;
  filter: string;
  defaultTimeframe?: Pick<SearchState, "endDate" | "startDate" | "duration">;
}) {
  const { token } = theme.useToken();
  const router = useRouter();
  const orgId = router.query.orgId as string;

  //validate filterable is a valid entity type
  if (
    !Object.values(EntityTypes).includes(
      filterable as (typeof EntityTypes)[keyof typeof EntityTypes]
    )
  ) {
    throw new Error(`Invalid filterable: ${filterable}`);
  }

  // Fetch all topics/digests
  const { links, loading } = useDeepLinks(DeepLinkType.NARRATIVE);
  const narratives = links.data || [];
  const timeframe = defaultTimeframe || { duration: "P7D" };

  // Filter digests that have a person filter
  const filteredNarratives = narratives.filter(
    (narrative): narrative is AnnotatedDeepLink<NarrativeV1State> => {
      return narrative?.json?.filterable?.includes(filterable) || false;
    }
  );

  const handleCardClick = (narrative: AnnotatedDeepLink<NarrativeV1State>) => {
    // Create the narrative state with person filter and timeframe
    const narrativeState = {
      n: narrative.name, // override name
      d:
        narrative.json.suggestedDigestIds ||
        narrative.json.digestIds.slice(
          0,
          Math.min(narrative.json.digestIds.length, 3)
        ), // selected digest
      o: { [filterable]: filter },
      tf: timeframe, // default timeframe
    };

    // Navigate to the rendered narrative page in a new tab
    const url = `/${orgId}/reports/${narrative.id}?${new URLSearchParams({
      s: serializeParams(narrativeState),
      mode: "render",
    }).toString()}`;
    window.open(url, "_blank");
  };

  return (
    <div style={{ padding: "20px" }}>
      <Flex
        wrap
        gap={16}
        style={{
          justifyContent: "flex-start",
        }}
      >
        {filteredNarratives.map((narrative, idx) => (
          <div
            key={idx}
            style={{
              width: "calc((100% - 64px) / 5)",
              minWidth: "calc((100% - 32px) / 3)",
              maxWidth: "300px",
            }}
            onClick={() => handleCardClick(narrative)}
          >
            <Hover
              hoverStyle={{
                borderColor: token.colorPrimaryBorderHover,
                backgroundColor: token.colorPrimaryBg,
                color: token.colorPrimary,
              }}
            >
              <Card
                size="small"
                style={{
                  height: 120,
                  cursor: "pointer",
                  borderColor: token.colorBorderSecondary,
                }}
              >
                <TextTruncated
                  lines={1}
                  lineHeight={20}
                  style={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: 600,
                    marginBottom: "4px",
                  }}
                >
                  {narrative.name}
                </TextTruncated>
                <TextTruncated
                  lines={3}
                  lineHeight={18}
                  style={{
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: token.colorTextDescription,
                  }}
                >
                  {narrative.description}
                </TextTruncated>
              </Card>
            </Hover>
          </div>
        ))}
      </Flex>

      {!loading && filteredNarratives.length === 0 && (
        <div style={{ textAlign: "center", color: token.colorTextDescription }}>
          No topics found with {filterable} filter
        </div>
      )}
      {loading && filteredNarratives.length === 0 && <Spin />}
    </div>
  );
}
